<template>
  <div class="bc-preview__wrapper">
    <system-card
      v-if="systemMessage"
      :system-message="systemMessage"
      :cb-func="loadCard"
    />
    <profile-top
      :name="personFullName"
      :position="cardValues.position"
      :department="cardValues.department"
      :company="cardValues.company"
    />
    <div class="bc-content">
      <up-card class="bc-card">
        <h2 class="bc-card__title">{{ $t('businessCardPreview.contacts') }}</h2>
        <template v-if="contacts.length">
          <div
            v-for="group in contacts"
            :key="group.key"
            class="bc-contact-group"
          >
            <div class="bc-contact-group__header">
              <span class="bc-contact-group__icon-wrap">
                <i :class="group.icon" />
              </span>
            </div>
            <div class="bc-contact-group__value-group">
              <div
                v-for="(value, i) in group.values"
                :key="i"
                class="bc-contact-group__value"
              >
                <span v-if="i === 0" class="bc-contact-group__label">{{ group.label }}</span>
                <a :href="group.toHref(value)" class="bc-contact-group__link">{{ value }}</a>
              </div>
            </div>
          </div>
        </template>
        <p v-else class="bc-no-contacts">{{ $t('businessCardPreview.no_contacts') }}</p>
      </up-card>
    </div>
    <div
      v-for="group in extendInfo"
      :key="group.name"
      class="bc-content"
    >
      <up-card class="bc-card">
        <h2 class="bc-card__title bc-card__title--question">{{ group.label }}</h2>
        <ul class="bc-extend-list">
          <li
            v-for="(value, i) in group.values"
            :key="i"
            class="bc-extend-list__item"
          >{{ value }}</li>
        </ul>
      </up-card>
    </div>
    <div class="row bc-actions">
      <template v-if="!isCreated && contacts.length">
        <div class="col-4">
          <up-button
            class="up-btn bc-card__btn btn-decline"
            @click="handleEdit"
          >{{ $t('businessCardPreview.btn_edit') }}</up-button>
        </div>
        <div class="col-8">
          <up-button
            class="up-btn bc-card__btn btn-success"
            :disabled="isSaving"
            @click="handleConfirm"
          >
            <span v-if="isSaving" class="bc-card__btn-spinner" />
            <span v-else>{{ $t('businessCardPreview.btn_confirm') }}</span>
          </up-button>
        </div>
      </template>
      <template v-else>
        <div class="col-12">
          <up-button
            class="up-btn bc-card__btn btn-decline"
            @click="handleEdit"
          >{{ $t('businessCardPreview.btn_edit') }}</up-button>
        </div>
      </template>
    </div>
    <p v-if="saveError" class="bc-save-error">{{ saveError }}</p>
  </div>
</template>

<script>
import appBaseNav from '@/components/mixins/appBaseNav';
import businessCard from '@/components/mixins/methodsBusinessCard';
import SystemCard from '@/components/shared/upCard/SystemCard.vue';
import {
  contactGroups, emptyCard, extendGroups, parseCard, personName, toPayload,
} from '@/components/mixins/businessCardFields';

export default {
  name: 'BusinessCardPreview',
  components: { SystemCard },
  props: {
    locale: { type: String },
  },
  data() {
    return {
      isCreated: false,
      isLoading: true,
      isSaving: false,
      systemMessage: null,
      saveError: null,
      // состав полей приходит с сервера — до загрузки визитка пустая
      card: emptyCard(),
    };
  },
  computed: {
    cardValues() {
      return this.card.values;
    },
    personFullName() {
      return personName(this.card.values);
    },
    contacts() {
      return contactGroups(this.card, (field) => this.fieldLabel(field));
    },
    extendInfo() {
      return extendGroups(this.card);
    },
  },
  mixins: [appBaseNav, businessCard],
  watch: {
    locale() {
      this.$store.commit('setPageTitle', this.$t('businessCardPreview.header_title'));
    },
  },
  mounted() {
    this.$store.commit('setPageTitle', this.$t('businessCardPreview.header_title'));
    this.loadCard();
  },
  methods: {
    loadCard() {
      this.isLoading = true;
      this.systemMessage = null;
      this.getMyBusinessCard()
        .then((data) => {
          this.isLoading = false;
          this.isCreated = data.is_created;
          // описатели дополнительных полей лежат рядом с data, а не внутри неё
          const parsed = parseCard(data.data, data.extend_data, this.$store.getters.getLang);
          if (!parsed) {
            this.systemMessage = this.$t('system.bad_connect');
            return;
          }
          this.card = parsed;
        })
        .catch(() => {
          this.isLoading = false;
          this.systemMessage = this.$t('system.bad_connect');
        });
    },
    fieldLabel(field) {
      return field.label || this.$t(field.labelKey);
    },
    handleConfirm() {
      this.isSaving = true;
      this.saveError = null;
      this.saveBusinessCard(toPayload(this.card))
        .then((data) => {
          this.isSaving = false;
          this.isCreated = data.is_created;
          const parsed = parseCard(data.data, data.extend_data, this.$store.getters.getLang);
          if (parsed) this.card = parsed;
        })
        .catch(() => {
          this.isSaving = false;
          this.saveError = this.$t('system.bad_connect');
        });
    },
    handleEdit() {
      this.$router.push({ name: 'businessCardEdit' });
    },
  },
};
</script>

<style lang="scss" scoped>
.bc-preview {
  &__wrapper {
    display: flex;
    flex-direction: column;
    max-width: 760px;
    margin: 0 auto;
    padding-bottom: 24px;
    gap: 12px;
  }
}
.bc-content {
  display: flex;
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  align-items: center;
  flex: 3 1 100%;
  align-self: stretch;
  padding: 0 12px;
}
.bc-card {
  border-radius: 14px;
  width: 100%;

  & > div {
    gap: 12px;
  }
}
.bc-card__title {
  text-transform: uppercase;
  font-size: 14px;
  color: #1c1c1c;
  margin: 0;
}
// Заголовок дополнительного поля — вопрос организатора целой строкой,
// капслоком он читается как крик.
.bc-card__title--question {
  text-transform: none;
  font-size: 15px;
  line-height: 1.35;
}
.bc-extend-list {
  margin: 0;
  padding-left: 18px;

  &__item {
    font-size: 15px;
    line-height: 1.35;
    color: #1f1f1f;

    & + & {
      margin-top: 6px;
    }
  }
}
.bc-actions {
  padding: 0 12px;
}
.bc-card__btn {
  height: 48px;
  border-radius: 14px;

  &.btn-decline {
    border-color: #1c1c1c;
    color: #1c1c1c;
  }
  &.btn-success {
    border-color: #ff957d;
    color: #ff957d;
  }
}
.bc-card__btn-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: bc-spin 0.7s linear infinite;
}
@keyframes bc-spin {
  to { transform: rotate(360deg); }
}
.bc-save-error {
  margin: 0;
  padding: 0 12px;
  font-size: 13px;
  color: #c0392b;
  text-align: center;
}
.bc-no-contacts {
  margin: 0;
  font-size: 14px;
  color: #8a9ab0;
}
.bc-contact-group {
  display: flex;
  flex-direction: row;
  gap: 12px;

  &__header {
    display: flex;
    align-items: start;
    gap: 12px;
  }

  &__icon-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: 1.5px solid #1c1c1c;
    color: #1c1c1c;
    font-size: 18px;
    flex-shrink: 0;
  }

  &__label {
    font-size: 12px;
    color: #8a9ab0;
  }

  &__value {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  &__value {
    font-size: 15px;
    color: #1f1f1f;
  }

  &__link {
    color: inherit;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
